import { Component, inject, Input } from '@angular/core';
import { finalize } from 'rxjs';
import { GetVersionDiff } from 'src/app/models/catalogs/catalog';
import { CatalogItemService } from 'src/app/services/catalog-item.service';
import { LoadingService } from 'src/app/services/loading.service';

type RowGroup = {
  rowId: number | null;
  rowName: string | null;
  changes: GetVersionDiff[];
};

type EntityGroup = {
  entity: string;
  rows: Record<string, RowGroup>;
};

@Component({
  selector: 'app-catalog-diff',
  standalone: true,
  templateUrl: './catalog-diff.component.html',
  styleUrls: ['./catalog-diff.component.css']
})
export class CatalogDiffComponent {
  @Input() modalId: string = 'diffModal';

  private catalogSvc = inject(CatalogItemService);
  loadingSvc = inject(LoadingService);

  diffs: GetVersionDiff[] = [];
  groupedDiffs: { entity: string; rows: RowGroup[] }[] = [];
  loading = false;

  open(catalogItemId: number, versionId: number) {
    this.loadDiffs(catalogItemId, versionId);

    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  private loadDiffs(catalogItemId: number, versionId: number) {
    this.loading = true;
    this.loadingSvc.show();

    this.catalogSvc.getVersionDiffs(catalogItemId, versionId)
      .pipe(finalize(() => this.loadingSvc.hide()))
      .subscribe({
        next: data => {
          this.diffs = data;

          const grouped = data.reduce((acc, d) => {
            if (!acc[d.entity]) {
              acc[d.entity] = { entity: d.entity, rows: {} };
            }
            const rowKey = d.rowId ?? 'noid';
            if (!acc[d.entity].rows[rowKey]) {
              acc[d.entity].rows[rowKey] = {
                rowId: d.rowId ?? null,
                rowName: d.rowName ?? null,
                changes: []
              };
            }
            acc[d.entity].rows[rowKey].changes.push(d);
            return acc;
          }, {} as Record<string, EntityGroup>);

          this.groupedDiffs = (Object.values(grouped) as EntityGroup[]).map(g => ({
            entity: g.entity,
            rows: Object.values(g.rows)
          }));

          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.loadingSvc.hide();
        }
      });
  }

  closeModal() {
    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      const modal = (window as any).bootstrap?.Modal?.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  trackEntity(index: number, item: { entity: string }) {
    return item.entity;
  }

  trackRow(index: number, item: RowGroup) {
    return (item.rowId ?? 'noid') + '-' + (item.rowName ?? 'noname');
  }

  trackChange(index: number, item: GetVersionDiff) {
    return `${item.field}-${item.rowId ?? 'noid'}`;
  }
}